import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Box,
  Stack,
} from "@mui/material";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import { useEffect, useMemo, useState } from "react";
import { VendorDTO } from "../../types/VendorDTO";
import AddInvoiceForm from "./AddInvoiceForm";
import AddInvoiceVatLines from "./AddInvoiceVatLines";
import { createDocument } from "../../api/documents";
import { getCurrencies } from "../../api/vendorBankAccounts";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type VatRate = 23 | 8 | 5 | 0 | "zw";

type AddInvoiceFormState = {
  vendor: VendorDTO | null;
  incomingDocNo: string;
  dueDate: string;
  documentDate: string;
  salesDate: string;
  currencyCode: string;
  attachment: File | null;
  priceType: "netto" | "brutto";
  vatLines: {
    vat: VatRate;
    amount: number;
    description: string;
  }[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: string;
  vendors: VendorDTO[];
  vendorsLoading: boolean;
};

function vatToNumber(vat: VatRate): number {
  return typeof vat === "number" ? vat : 0;
}

function createEmptyForm(): AddInvoiceFormState {
  return {
    vendor: null,
    incomingDocNo: "",
    dueDate: "",
    documentDate: "",
    salesDate: "",
    currencyCode: "",
    attachment: null,
    priceType: "netto",
    vatLines: [
      { vat: 23, amount: 0, description: "" },
      { vat: 8, amount: 0, description: "" },
      { vat: 5, amount: 0, description: "" },
      { vat: 0, amount: 0, description: "" },
      { vat: "zw", amount: 0, description: "" },
    ],
  };
}

function normalizeCurrencies(data: any): string[] {
  if (!Array.isArray(data)) return [];

  const values = data
    .map((item) => {
      if (typeof item === "string") return item;

      return (
        item?.code ??
        item?.currencyCode ??
        item?.Code ??
        item?.CurrencyCode ??
        null
      );
    })
    .filter((item): item is string => !!item);

  return Array.from(new Set(values));
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };

    reader.onerror = () => reject(new Error("Nie udało się odczytać pliku"));
    reader.readAsDataURL(file);
  });
}

export default function AddInvoiceModal({
  open,
  onClose,
  onSuccess,
  companyId,
  vendors,
  vendorsLoading,
}: Props) {
  const { t } = useAppTranslation("common");

  const [form, setForm] = useState<AddInvoiceFormState>(createEmptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  const [currencies, setCurrencies] = useState<string[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        const data = await getCurrencies(companyId);

        if (cancelled) return;

        const normalized = normalizeCurrencies(data);
        setCurrencies(normalized);

        setForm((prev) => {
          if (prev.currencyCode) return prev;
          if (normalized.includes("PLN")) {
            return { ...prev, currencyCode: "PLN" };
          }
          if (normalized.length > 0) {
            return { ...prev, currencyCode: normalized[0] };
          }
          return prev;
        });
      } catch {
        if (!cancelled) {
          setCurrencies([]);
        }
      } finally {
        if (!cancelled) {
          setCurrenciesLoading(false);
        }
      }
    };

    loadCurrencies();

    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const totals = useMemo(() => {
    let net = 0;
    let gross = 0;

    for (const line of form.vatLines) {
      if (form.priceType === "netto") {
        net += line.amount;

        if (line.vat === "zw" || line.vat === 0) {
          gross += line.amount;
        } else {
          gross += line.amount * (1 + vatToNumber(line.vat) / 100);
        }
      } else {
        gross += line.amount;

        if (line.vat === "zw" || line.vat === 0) {
          net += line.amount;
        } else {
          net += line.amount / (1 + vatToNumber(line.vat) / 100);
        }
      }
    }

    return {
      net,
      gross,
    };
  }, [form.vatLines, form.priceType]);

  const hasAnyVatAmount = form.vatLines.some((l) => l.amount > 0);
  const currencyLabel = form.currencyCode || "PLN";

  const isFormValid =
    !!form.vendor &&
    form.incomingDocNo.trim() !== "" &&
    form.dueDate !== "" &&
    form.documentDate !== "" &&
    form.currencyCode.trim() !== "" &&
    hasAnyVatAmount;

  const buildPayload = async () => {
    const lines = form.vatLines
      .filter((l) => l.amount > 0)
      .map((l) => ({
        description: l.description || `VAT ${l.vat}`,
        quantity: 1,
        directUnitCost:
          form.priceType === "netto"
            ? l.amount
            : l.vat === "zw" || l.vat === 0
              ? l.amount
              : l.amount / (1 + vatToNumber(l.vat) / 100),
        vat: l.vat === "zw" ? 0 : l.vat,
      }));

    let attachmentName: string | null = null;
    let attachmentContent: string | null = null;

    if (form.attachment) {
      attachmentName = form.attachment.name;
      attachmentContent = await fileToBase64(form.attachment);
    }

    return {
      vendorNo: form.vendor!.no,
      incomingDocNo: form.incomingDocNo,
      dueDate: form.dueDate,
      documentDate: form.documentDate,
      salesDate: form.salesDate || undefined,
      currencyCode: form.currencyCode,
      attachmentName: attachmentName || undefined,
      attachmentContent: attachmentContent || undefined,
      amountExclVAT: Number(totals.net.toFixed(2)),
      amountInclVAT: Number(totals.gross.toFixed(2)),
      lines,
    };
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const payload = await buildPayload();
      await createDocument(companyId, payload);

      onClose();
      onSuccess?.();
      setSuccessOpen(true);
      setForm(createEmptyForm());
    } catch (e: any) {
      setError(e.message || "Błąd dodawania faktury");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={submitting}
        PaperProps={{
          sx: { borderRadius: 3, overflow: "hidden" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 2,
          }}
        >
          <NoteAddIcon />
          <Box>
            <Typography variant="h6" fontWeight={600} color="inherit">
              {t("Dodaj nową fakturę")}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {t("Wypełnij dane dokumentu i pozycje VAT")}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 3 }}>
          {vendorsLoading ? (
            <CircularProgress />
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <AddInvoiceForm
                vendors={vendors}
                currencies={currencies}
                currenciesLoading={currenciesLoading}
                value={{
                  vendor: form.vendor,
                  incomingDocNo: form.incomingDocNo,
                  dueDate: form.dueDate,
                  documentDate: form.documentDate,
                  salesDate: form.salesDate,
                  currencyCode: form.currencyCode,
                  attachment: form.attachment,
                }}
                onChange={(header) =>
                  setForm((prev) => ({
                    ...prev,
                    ...header,
                  }))
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.priceType === "brutto"}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        priceType: e.target.checked ? "brutto" : "netto",
                      }))
                    }
                  />
                }
                label={
                  form.priceType === "netto"
                    ? t("Ceny to kwoty NETTO (zaznacz jeśli BRUTTO)")
                    : t("Ceny to kwoty BRUTTO")
                }
              />

              <AddInvoiceVatLines
                priceType={form.priceType}
                currencyCode={form.currencyCode}
                lines={form.vatLines}
                onChange={(vatLines) =>
                  setForm((prev) => ({
                    ...prev,
                    vatLines,
                  }))
                }
              />

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#F8FAFC",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body1" fontWeight={600}>
                    {t("Kwota netto")}: {totals.net.toFixed(2)} {currencyLabel}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {t("Kwota brutto")}: {totals.gross.toFixed(2)}{" "}
                    {currencyLabel}
                  </Typography>
                </Stack>
              </Box>

              {!hasAnyVatAmount && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {t(
                    "Uzupełnij przynajmniej jedną pozycję VAT, kwota na fakturze nie może być zerowa"
                  )}
                </Alert>
              )}

              {submitting && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t("Trwa dodawanie faktury...")}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={submitting}>
            {t("Anuluj")}
          </Button>

          <Button
            variant="contained"
            disabled={!isFormValid || submitting}
            onClick={handleSubmit}
          >
            {submitting ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} color="inherit" />
                <span>{t("Dodawanie...")}</span>
              </Stack>
            ) : (
              t("Dodaj fakturę")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={3000}
        onClose={() => setSuccessOpen(false)}
      >
        <Alert severity="success" onClose={() => setSuccessOpen(false)}>
          {t("Faktura została dodana pomyślnie!")}
        </Alert>
      </Snackbar>
    </>
  );
}